import { getApps, initializeApp } from "firebase/app";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { fetchAndActivate, getRemoteConfig } from "firebase/remote-config";

const FIREBASE_CONFIG_LOCAL_STORAGE_KEY = "firebase-config";

let getProfileInstance = null;
const firebaseAppConfig = async () => {
    const result = await fetch("/get-config", {
        method: "POST",
        body: JSON.stringify({
            userId: window.currentUserId,
        }),
    });

    const json = await result.json();
    localStorage.setItem(FIREBASE_CONFIG_LOCAL_STORAGE_KEY, json.data.string);
    return json.data.string;
};

// Firebase App instance
export const firebaseApp = async () => {
    const localStorageConfig = localStorage.getItem(FIREBASE_CONFIG_LOCAL_STORAGE_KEY);
    let firebaseConfigJson = null;

    if (localStorageConfig) {
        firebaseConfigJson = JSON.parse(Buffer.from(localStorageConfig, "base64").toString("utf-8"));
    } else {
        const config = Buffer.from(await firebaseAppConfig(), "base64").toString("utf-8");
        firebaseConfigJson = JSON.parse(config);
    }

    if (getApps()?.length) {
        return getApps()[0];
    }

    return initializeApp(firebaseConfigJson);
};

const oneHourInMillisecond = 3600000;
// Get Firebase Remote Config
export const getFirebaseRemoteConfig = async () => {
    const app = await firebaseApp();
    const remoteConfiguration = getRemoteConfig(app);
    if (remoteConfiguration.settings.minimumFetchIntervalMillis !== oneHourInMillisecond) {
        remoteConfiguration.settings = {
            minimumFetchIntervalMillis: oneHourInMillisecond, // Set minimum fetch interval to 0 milliseconds
        };
    }
    await fetchAndActivate(remoteConfiguration);
    return remoteConfiguration;
};

// Initialize Firebase
const getFirebaseApp = async () => new Promise(async (resolve, reject) => {
    const app = await firebaseApp();
    const auth = getAuth(app);

    auth.onAuthStateChanged((user) => {
        resolve(user);
    });
});

export const getIdToken = async () => {
    try {
        const app = await firebaseApp();
        const auth = getAuth(app);
        if (!auth) {
            return null;
        }
        const user = auth.currentUser;
        if (user) {
            return await user.getIdToken(true);
        }
    } catch (error) {
        return null;
    }
    return null;
};

export const getProfileFirebase = async (callback) => {
    const app = await firebaseApp();
    const auth = getAuth(app);
    if (!auth) {
        return null;
    }
    if (getProfileInstance) {
        return getProfileInstance;
    }

    getProfileInstance = new Promise((resolve) => {
        const unsubscribe = onAuthStateChanged(auth, (fUser) => {
            if (fUser) {
                fUser?.getIdTokenResult().then(async (res) => {
                    const loggedInUser = {
                        authtoken: res?.token,
                        email: fUser?.email?.toString(),
                        emailVerified: fUser?.emailVerified,
                        phoneNumber: fUser.phoneNumber?.toString(),
                        firstName: res?.claims?.metadata?.firstName
                            ?.toString(),
                        lastName: res?.claims?.metadata?.lastName
                            ?.toString(),
                        agree: res?.claims?.metadata?.aggree,
                        marketing: res?.claims?.metadata?.marketing,
                        role: res?.claims?.role?.toString(),
                        userId: res?.claims?.metadata?.userId?.toString(), // legacy user id
                        businessId:
                            res?.claims?.metadata?.businessId?.toString(), // legacy business id
                    };

                    if (callback) {
                        callback(loggedInUser);
                    }

                    const token = await getIdToken();
                    getProfileInstance = null;
                    resolve({
                        user: loggedInUser,
                        token,
                    });
                });
            } else {
                getProfileInstance = null;
                resolve(null);
            }
        });
        // eslint-disable-next-line no-promise-executor-return
        return () => unsubscribe();
    });
    return getProfileInstance;
};

export default getFirebaseApp;
